import React from 'react';
import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import { CheckCircle, Warning, AccessTime } from '@mui/icons-material';
import { StatusResponse } from '../../types/matchReport.ts';

interface ReconciliationBannerProps {
  status: StatusResponse;
  onRefresh?: () => void;
}

export const ReconciliationBanner: React.FC<ReconciliationBannerProps> = ({ status }) => {
  const { yourReport, opponentReport, reconciliationStatus, gameStatus, gameInfo } = status;

  const getStatusIcon = (reportStatus: string) => {
    switch (reportStatus) {
      case 'Submitted':
        return <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'Missing':
        return <AccessTime sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return <Warning sx={{ fontSize: 16, color: 'grey.500' }} />;
    }
  };

  const getReconciliationAlert = () => {
    if (gameStatus === 'Finalized') {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Game Finalized</AlertTitle>
          This match has been officially finalized. No further changes can be made.
        </Alert>
      );
    }

    switch (reconciliationStatus) {
      case 'Matched':
        if (yourReport.status === 'Submitted' && opponentReport.status === 'Submitted') {
          return (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Reports Match!</AlertTitle>
              Both teams have submitted reports with matching scores:
              <strong> {gameInfo?.homeTeamName || 'Home'} {yourReport.homeGoals} - {yourReport.awayGoals} {gameInfo?.awayTeamName || 'Away'}</strong>
            </Alert>
          );
        }
        break;

      case 'Mismatch':
        return (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Score Mismatch</AlertTitle>
            The submitted reports have different scores:
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Your report: {gameInfo?.homeTeamName || 'Home'} {yourReport.homeGoals} - {yourReport.awayGoals} {gameInfo?.awayTeamName || 'Away'}
              </Typography>
              <Typography variant="body2">
                Opponent report: {gameInfo?.homeTeamName || 'Home'} {opponentReport.homeGoals} - {opponentReport.awayGoals} {gameInfo?.awayTeamName || 'Away'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please coordinate with the opposing team to resolve this discrepancy.
            </Typography>
          </Alert>
        );

      case 'Waiting':
        if (yourReport.status === 'Submitted') {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Waiting for Opponent</AlertTitle>
              Your report has been submitted successfully. Waiting for the opposing team to submit their report.
            </Alert>
          );
        } else if (opponentReport.status === 'Submitted') {
          return (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Opponent Report Submitted</AlertTitle>
              The opposing team has submitted their report. Please submit your report to complete the process.
            </Alert>
          );
        }
        break;

      default:
        break;
    }

    return null;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {getReconciliationAlert()}
      
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(yourReport.status)}
            <Typography variant="body2" fontWeight="medium">
              Your Report
            </Typography>
            <Chip
              label={yourReport.status}
              size="small"
              color={yourReport.status === 'Submitted' ? 'success' : 'warning'}
              variant="outlined"
              clickable={false}
              onClick={() => {}}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(opponentReport.status)}
            <Typography variant="body2" fontWeight="medium">
              Opponent Report
            </Typography>
            <Chip
              label={opponentReport.status}
              size="small"
              color={opponentReport.status === 'Submitted' ? 'success' : 'warning'}
              variant="outlined"
              clickable={false}
              onClick={() => {}}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Game Status:
          </Typography>
          <Chip
            label={gameStatus}
            size="small"
            color={gameStatus === 'Finalized' ? 'success' : 'default'}
            variant={gameStatus === 'Finalized' ? 'filled' : 'outlined'}
            clickable={false}
            onClick={() => {}}
          />
        </Box>
      </Box>

      {/* Show submitted scores if available */}
      {(yourReport.status === 'Submitted' || opponentReport.status === 'Submitted') && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Submitted Scores:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {yourReport.status === 'Submitted' && (
              <Typography variant="body2">
                Your team: {gameInfo?.homeTeamName || 'Home'} {yourReport.homeGoals} - {yourReport.awayGoals} {gameInfo?.awayTeamName || 'Away'}
              </Typography>
            )}
            {opponentReport.status === 'Submitted' && (
              <Typography variant="body2">
                Opponent: {gameInfo?.homeTeamName || 'Home'} {opponentReport.homeGoals} - {opponentReport.awayGoals} {gameInfo?.awayTeamName || 'Away'}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReconciliationBanner;